const SUPPORTED_SAMPLE_TYPES = new Set([
  'active_calories',
  'body_weight',
  'distance',
  'heart_rate',
  'resting_heart_rate',
  'sleep',
  'sleep_duration',
  'steps',
  'total_calories',
]);

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const compactObject = (value) =>
  Object.fromEntries(
    Object.entries(value || {}).filter(([, item]) => item !== null && item !== undefined && item !== '')
  );

const stableSourceRecordId = (parts) =>
  parts
    .map((part) => String(part ?? '').trim())
    .filter(Boolean)
    .join(':')
    .slice(0, 240);

const recordedAtForSample = (sample) =>
  sample?.end_at || sample?.recorded_at || sample?.time || sample?.start_at || null;

const metricTypeForSample = (sample) => {
  const metricType = String(sample?.metric_type || '').trim().toLowerCase();
  if (metricType === 'sleep') return 'sleep_duration';
  if (
    metricType === 'heart_rate' &&
    String(sample?.aggregation || '').trim().toLowerCase() === 'avg' &&
    toFiniteNumber(sample?.metadata?.measurements_count) > 1
  ) {
    return 'average_heart_rate';
  }
  return metricType;
};

const safeMetricMetadata = (sample) =>
  compactObject({
    aggregation: sample?.aggregation,
    native_metric_type: sample?.metric_type,
    timezone: sample?.timezone,
    max_heart_rate_bpm: toFiniteNumber(sample?.metadata?.max_heart_rate_bpm),
    measurements_count: toFiniteNumber(sample?.metadata?.measurements_count),
  });

const healthConnectSampleToMetric = (sample, deviceId) => {
  const nativeType = String(sample?.metric_type || '').trim().toLowerCase();
  if (!SUPPORTED_SAMPLE_TYPES.has(nativeType)) return null;

  const value = toFiniteNumber(sample?.value);
  const recordedAt = recordedAtForSample(sample);
  const unit = String(sample?.unit || '').trim();
  const metricType = metricTypeForSample(sample);
  if (value === null || !recordedAt || !metricType || !unit) return null;

  const sourceRecordId =
    sample?.provider_sample_id ||
    sample?.source_record_id ||
    stableSourceRecordId([
      'health_connect',
      metricType,
      sample?.start_at,
      sample?.end_at,
      recordedAt,
      value,
      unit,
    ]);

  return compactObject({
    source_record_id: sourceRecordId,
    device_id: sample?.device_id || sample?.deviceId || deviceId,
    recorded_at: recordedAt,
    metric_type: metricType,
    value,
    unit,
    metadata: safeMetricMetadata(sample),
  });
};

const equipmentTypeForWorkout = (workoutType) => {
  const normalized = String(workoutType || '').trim().toLowerCase();
  if (normalized === 'running') return 'treadmill';
  if (normalized === 'cycling') return 'stationary_bike';
  return 'other';
};

const durationSecondsForWorkout = (workout) => {
  const durationMin = toFiniteNumber(workout?.duration_min);
  if (durationMin !== null) return Math.max(0, Math.round(durationMin * 60));
  const start = Date.parse(workout?.start_at);
  const end = Date.parse(workout?.end_at);
  if (Number.isFinite(start) && Number.isFinite(end) && end >= start) {
    return Math.round((end - start) / 1000);
  }
  return null;
};

const safeWorkoutMetadata = (workout) =>
  compactObject({
    native_workout_type: workout?.workout_type,
    timezone: workout?.timezone,
    health_connect_exercise_type: workout?.raw_metadata?.health_connect_exercise_type,
    steps: toFiniteNumber(workout?.steps),
  });

const healthConnectWorkoutToImportWorkout = (workout, deviceId) => {
  const startedAt = workout?.start_at;
  const endedAt = workout?.end_at;
  if (!startedAt || !endedAt) return null;

  const durationSec = durationSecondsForWorkout(workout);
  const calories =
    toFiniteNumber(workout?.active_calories_kcal) ?? toFiniteNumber(workout?.total_calories_kcal);
  const distanceM = toFiniteNumber(workout?.distance_m);
  const heartRateAvg = toFiniteNumber(workout?.avg_heart_rate_bpm);
  const heartRateMax = toFiniteNumber(workout?.max_heart_rate_bpm);
  const workoutType = String(workout?.workout_type || '').trim().toLowerCase();
  const sourceRecordId =
    workout?.provider_workout_id ||
    workout?.source_record_id ||
    stableSourceRecordId([
      'health_connect',
      'workout',
      workoutType,
      startedAt,
      endedAt,
      durationSec,
      calories,
      distanceM,
    ]);

  return compactObject({
    source_record_id: sourceRecordId,
    device_id: workout?.device_id || workout?.deviceId || deviceId,
    equipment_type: equipmentTypeForWorkout(workoutType),
    started_at: startedAt,
    ended_at: endedAt,
    completed: true,
    duration_sec: durationSec,
    distance_m: distanceM,
    calories,
    heart_rate_avg: heartRateAvg !== null ? Math.round(heartRateAvg) : null,
    heart_rate_max: heartRateMax !== null ? Math.round(heartRateMax) : null,
    metadata: safeWorkoutMetadata(workout),
  });
};

export const buildHealthConnectImportPayload = (nativePayload, options = {}) => {
  const deviceId = options.device_id || options.deviceId;
  const appInstanceId = options.app_instance_id || options.appInstanceId || deviceId;
  const samples = Array.isArray(nativePayload?.samples) ? nativePayload.samples : [];
  const workouts = Array.isArray(nativePayload?.workouts) ? nativePayload.workouts : [];

  return {
    source: 'health_connect',
    provider: 'health_connect',
    device_id: deviceId,
    platform: 'android',
    app_instance_id: appInstanceId,
    background_sync_enabled: Boolean(options.background_sync_enabled || options.backgroundSyncEnabled),
    metrics: samples
      .map((sample) => healthConnectSampleToMetric(sample, deviceId))
      .filter(Boolean),
    workouts: workouts
      .map((workout) => healthConnectWorkoutToImportWorkout(workout, deviceId))
      .filter(Boolean),
  };
};

export const healthConnectLastProviderTimestamp = (nativePayload) => {
  const records = [
    ...(Array.isArray(nativePayload?.workouts) ? nativePayload.workouts : []),
    ...(Array.isArray(nativePayload?.samples) ? nativePayload.samples : []),
  ];
  const timestamps = records
    .flatMap((record) => [record?.end_at, record?.recorded_at, record?.time, record?.start_at])
    .map((value) => Date.parse(value))
    .filter((value) => Number.isFinite(value));
  if (!timestamps.length) return null;
  return new Date(Math.max(...timestamps)).toISOString();
};
